"use strict";

import jQuery from 'jquery';

/**
 * Globale Instanz der {@link gc.gis.GCApplication}-Klasse.
 * @instance
 */
gc.gis.iface = undefined;

/**
 * Kartenbibliotheken
 * @enum {string}
 */
gc.gis.maplib = {
    /**
	 * Openlayer in der aktuellen Version.
     */
	OL: 'ol',
    /**
     * Leaflet in der aktuellen Version.
     */
	LEAFLET: 'Leaflet'
};

/**
 * Umgebungseigenschaften
 * @type {Object<string,Object<string,string>>}
 */
gc.gis.env = {
	/**
	 * Geräte-Kategorien.
	 * @enum {string}
	 */
	device: {
		/**
		 * Desktop-Gerät
		 */
		DESKTOP: 'desktop',
		/**
		 * Smartphone, Tablet, etc.
		 */
		MOBILE: 'mobile'
	}
};

/**
 * Applikations-Events
 * @enum {string}
 */
gc.gis.GCApplicationEvent = {
	/**
	 * Event-Typ, wenn ein Modul geladen wurde.
	 * @event gc.gis.GCApplicationEvent#load:module
	 * @type {Object}
	 * @property {string} alias Modulalias
	 * @property {gc.gis.module.GCAbstractModule} instance Modulinstanz
	 */
	MODULE_LOADED: 'load:module',
	/**
	 * Event-Typ, wenn eine Ansicht geladen wurde.
	 * @event gc.gis.GCApplicationEvent#load:view
	 * @type {Object}
	 * @property {Object} Ansichten-Objekt
	 */
	VIEW_LOADED:   'load:view'
};

/**
 * @classdesc
 * Applikationsklasse
 *
 * @class
 * @extends gc.gis.GCObservable
 * @memberof gc.gis
 * @author Anton Popov <a.popov@brain-scc.de>
 *
 * @param {Object} options Aufrufoptionen
 * @param {string} options.id HTML-ID des Kontainerelements
 * @param {Object<string,string>} options.requests Request-URLs
 * @param {Object<string,Object>} options.defaults Grundkonfiguration
 * @param {Array<Object>} options.modules zu ladende Modulkonfigurationen
 */
gc.gis.GCApplication = function(options)
{
	gc.gis.GCObservable.call(this);

	// Instanz global registrieren
	gc.gis.iface = this;

	/**
	 * Debug-Modus-Flag
	 *
	 * @public
	 * @type {boolean}
	 */
	this.DEBUG = true;

    /**
	 * Konstante der Maßstabszahl, die beim Zoomen z.B. zu einem Objekt nicht unterschritten werden soll
	 *
	 * @public
     * @type {number}
     */
	this.ZOOM_TO_MINSCALE = 1000;

	/**
	 * @public
	 * @type {Object<string,*>}
	 */
	this.config = options;

	// Memberdeklarationen
	/**
	 * HTML-ID des Root-Containers.
	 *
	 * @public
	 * @type {string}
     */
	this.id = options.id;
    /**
     * FE-Flag.
     *
     * @public
     * @type {boolean}
     */
    this.fe = options.fe;
	/**
	 * jQuery-Element des Root-Containers.
	 *
	 * @public
	 * @type {jQuery}
     */
	this.element = undefined;
	/**
	 * @public
	 * @type {Object<string,*>}
	 */
	this.env = {};
	/**
	 * @public
	 * @type {gc.gis.log.GCLog}
	 */
	this.log = undefined;
	/**
	 * @public
	 * @type {gc.gis.util.GCRequestManager}
     */
	this.request = new gc.gis.util.GCRequestManager();
	/**
	 * @public
	 * @type {Object<string,string>}
	 */
	this.requests = options.requests || {};
	/**
	 * @public
	 * @type {gc.gis.ui.GCUserInterface}
	 */
	this.ui = undefined;
	/**
	 * Factory-Instanz für Kartenbibliothek
	 *
	 * @public
	 * @type {gc.gis.factory.ObjectFactory}
	 */
	this.mapFactory = undefined;
	/**
	 * Factory-Instanz für Styles in Abhängigkeit der Kartenbibliothek.
	 *
	 * @public
	 * @type {gc.gis.factory.StyleFactory}
	 */
	this.styleFactory = undefined;
	/**
	 * Factory-Instanz für Kartenbibliothek
	 *
	 * @public
	 * @type {gc.gis.factory.ServiceFactory}
	 */
	this.serviceFactory = undefined;
    /**
	 * Genutzte Kartenbibliothek (zurzeit statisch Openlayers).
	 *
	 * @public
     * @type {string}
     */
	this.mapLib = gc.gis.maplib.OL;
	/**
	 * Karten-Instanz
	 *
	 * @public
	 * @type {gc.gis.map.GCMapView}
	 */
	this.map = undefined;
	/**
	 * @public
	 * @type {Object<string,gc.gis.module.GCAbstractModule>}
	 */
	this.modules = {};
	/**
	 * Initialer Kartenausschnitt
	 *
	 * @public
	 * @type {gc.gis.map.GCMapViewport}
	 */
	this.initialViewport = undefined;
	/**
	 * @public
	 * @type {gc.gis.map.GCMapViewport}
	 */
	this.currentViewport = undefined;
	/**
	 * Letzte Ansicht
	 *
	 * @public
	 * @type {Object<string,*>}
	 */
	this.lastViewport = undefined;

	// Initialisieren
	this.init();
};
gc.gis.GCApplication.prototype = Object.create( gc.gis.GCObservable.prototype );

/**
 * @public
 */
gc.gis.GCApplication.prototype.init = function()
{
	this._initLogger();
	try
	{
		// Service-Factory erzeugen
		this.serviceFactory = new gc.gis.factory.ServiceFactory(this.request, this.requests);

		this.log.info( this, 'Initialisiere Browserumgebung ... ');
		this._initEnv();
		this.log.info( this, 'Initialisiere Benutzeroberfläche ... ');
		this._initUI();
		this.log.info( this, 'Initialisiere Ansicht ... ');
		this._initViewport();
		this.log.info( this, 'Initialisiere Karte ... ');
		this._initMap();
		this.log.info( this, 'Initialisiere Module ... ');
		this._initModules();
		this.log.info( this, 'Bereit.');
		this.ui.setNotificationsActive(true);

		this._initBackgorundLayers();

		// Startansicht laden
		this.log.info(this, 'Lade Startansicht ... ');
		this._initInitialView();
	}
	catch(err)
	{
		this.log.error(this, err);
	}
};

/**
 * @public
 */
gc.gis.GCApplication.prototype.isFE = function()
{
    return this.fe;
};

/**
 * @public
 */
gc.gis.GCApplication.prototype.getElement = function()
{
	return this.element;
};

/**
 * @protected
 */
gc.gis.GCApplication.prototype._initEnv = function()
{
	var device = (/android|blackberry|iemobile|ipad|iphone|opera mini|webos/i)
		.test( navigator.userAgent ) ?
		gc.gis.env.device.MOBILE : gc.gis.env.device.DESKTOP;

	var can_clipboard = document.queryCommandSupported
		&& document.queryCommandSupported('copy') === true;

	var can_geolocate = ('geolocation' in navigator ) === true;

	var can_fullscreen = ('function' === typeof document.exitFullscreen)
		|| ('function' === typeof document.mozCancelFullScreen)
		|| ('function' === typeof document.webkitExitFullscreen)
        || ('function' === typeof document.msExitFullscreen);

	var can_fileapi = false;	// FIXME

	this.env = {
		device:               device,
		dpi:                  96,
		dpm:                  96*39.3701,	// 1dpi = 39.3701dpm
        devicePixelRatio:	  window.devicePixelRatio || 1,
		fullscreenAvailable:  can_fullscreen,
		geolocationAvailable: can_geolocate,
		storageAvailable:     can_fileapi,
		clipboardAvailable:   can_clipboard
	};
};

/**
 * @protected
 */
gc.gis.GCApplication.prototype._initLogger = function()
{
	var self = this;

	this.log = new gc.gis.log.GCLog();
	this.log.on( gc.gis.log.GCLogEvent.MESSAGE, this, function( evt ) {
		self.onLogEvent( evt );
	});
};

/**
 * @public
 * @param {gc.gis.log.GCLogEvent} evt Log-Event
 */
gc.gis.GCApplication.prototype.onLogEvent = function( evt )
{
	if( !evt )
	{
		return;
	}

	var type;

	try {
		switch (evt.level) {
			case gc.gis.log.GCLogLevel.INFO:
				type = gc.gis.ui.GCNotificationType.INFO;
				console.info(evt.time + ': INFO  ' + evt.context + ': ' + evt.message);
				break;

			case gc.gis.log.GCLogLevel.WARNING:
				type = gc.gis.ui.GCNotificationType.WARNING;
				console.warn(evt.time + ': WARN  ' + evt.context + ': ' + evt.message);
				break;

			case gc.gis.log.GCLogLevel.ERROR:
				type = gc.gis.ui.GCNotificationType.ERROR;
				if (evt.message instanceof Error) {
					console.error(evt.message.toString());
					console.error(evt.message.stack);
				}
				else {
					console.error(evt.time + ': ERROR ' + evt.context + ': ' + evt.message);
				}
				break;

			case gc.gis.log.GCLogLevel.DEBUG:
				console.debug(evt.time + ': DEBUG ' + evt.context + ': ' + evt.message);
				break;

			default:
		}

		if (this.ui && type && evt.notify && evt.level !== gc.gis.log.GCLogLevel.DEBUG) {
			this.ui.notify({
				type: type,
				message: evt.message
			});
		}
	}
	catch (err) {
		// !!! Exception hier fangen, ansonsten kommt es in gc.gis.GCObservable.prototype._fireEvent zu Endlos-Rekursion
		// da bei jeder Exception ein Log-Eintrag geschrieben wird
		console.error(err);
	}
};

/**
 * @protected
 */
gc.gis.GCApplication.prototype._initUI = function()
{
	// Container ermitteln/erstellen
	this.log.debug( this, 'INIT: Setting up basic user interface... ');
	this.log.debug( this, 'INIT: Looking for container... ');
	this.element = jQuery('#'+ this.id );	// FIXME: move to this.ui
	if( !this.element.length )
	{
		this.log.debug( this, 'INIT: Creating new container... ');
        this.element = jQuery('<div>', {
            id: this.id
        });
        jQuery('body').append( this.element );
	}
	else
	{
		this.log.debug( this, 'INIT: Looking for container... Done (found ' + this.element.length + ').');
	}

	this.log.debug( this, 'INIT: Setting up basic user interface... Done.');

	this.ui = new gc.gis.ui.GCUserInterface({
		id: this.id,
		notificationTimeout: 5000
	});

    this.ui.init();

    // ggf. Menü einklappen beim Start
    if (this.config.defaults.menuCollapsed)
    {
        this.ui.sidebar.collapse();
    }
};

/**
 * @protected
 */
gc.gis.GCApplication.prototype._initModules = function()
{
	if (this.config.modules) {
		for (var i = 0; i < this.config.modules.length; i++) {
			this.loadModule(this.config.modules[i]);
		}
	}
};


/**
 * @public
 * @param {Object<string,*>} config Modul-Config
 * @return {boolean} <code>true</code> bei Erfolg, ansonsten <code>false</code>
 */
gc.gis.GCApplication.prototype.loadModule = function(config)
{
	gc.gis.assert.isSet(config.className).isSet(config.alias);

	try
	{
		this.log.debug( this, 'Lade Modul "'+ config.className +'" nach "'+ config.target +'"... ');
		if( config.className )
		{
			var className = eval( config.className );
			if( className )
			{
				config.options.alias = config.alias;
                config.options.target = config.target;

				var module = new className(config.options);
				this.modules[config.alias] = module;

				this._fireEvent( gc.gis.GCApplicationEvent.MODULE_LOADED, {
					alias: config.alias,
					instance: module
				});
				this.log.info( this, 'Modul "'+ config.alias +'" geladen.');

				return true;
			}
			else
			{
				this.log.error( this, 'Modul existiert nicht "'+ className +'"');
			}
		}
		else
		{
			this.log.error( this, 'Fehler beim Laden von Modul "'+ config.className +'"');

			return false;
		}
	}
	catch( err )
	{
		this.log.error( this, err );
	}

	return false;
};


/**
 * @protected
 */
gc.gis.GCApplication.prototype._initViewport = function()
{
	// Fallback
	this.initialViewport = {
		projection: this.config.defaults.defaultCRS || 'EPSG:4326',
		rotation: 0,
		layers: []
	};

	this.lastViewport = this.initialViewport;
};

/**
 * @protected
 */
gc.gis.GCApplication.prototype._initMap = function()
{
	var options_map = {
		target:                 'gc_map_view',
		maxExtent:              this.config.defaults.maxExtent,     // Extent für Gebietseinschränkung
		constrainOnlyCenter:    gc.gis.GCUtil.parseBool(this.config.defaults.constrainOnlyCenter, false),    // Flag für weiche Einschränkung (Mittelpunkt innerhalb Extent)
		rotation:               this.initialViewport.rotation,
		layers:                 this.initialViewport.layers,
		projection:             this.initialViewport.projection,
		availableScales:        this.config.defaults.scales || null,
		availableProjections:   this.config.defaults.availableCRS || null
	};

	switch (this.mapLib)
	{
		case gc.gis.maplib.OL:
			this.mapFactory = new gc.gis.factory.OLFactory();
			this.styleFactory = new gc.gis.factory.OLStyleFactory();
			break;
		case gc.gis.maplib.LEAFLET:
			// TODO
			break;

	}
	this.map = new gc.gis.map.GCMapView(options_map);
	this.__initMapControls();
};

/**
 * @private
 */
gc.gis.GCApplication.prototype.__initMapControls = function()
{
	var controls = [
		new gc.gis.control.GCDrawingAreaClear({
			target: this.ui.mapToolbarZoom,
			map: this.map
		}),
		new gc.gis.control.GCMapProjectionSwitcher({
			id: '#gc_map_projection_switcher',
			className: 'gc-footer-component form-inline',
			target: '#gc_footer_auxiliary',
			hideable: false,
			map: this.map
		}),
		new gc.gis.control.GCMapScaleSwitcher({
			id: '#gc_map_scale_switcher',
			className: 'gc-footer-component form-inline',
			target: '#gc_footer_main',
			map: this.map,
			scales: this.map.getAvailableScales(),
			userDefinedScale: gc.gis.GCUtil.parseBool(this.config.defaults.userDefinedScale, true),
			restrictUserDefinedScale: gc.gis.GCUtil.parseBool(this.config.defaults.restrictUserDefinedScale, false)
		}),
		new gc.gis.control.OLMousePosition({
			id: '#gc_map_mouse_position',
			className: 'gc-footer-component',
			target: '#gc_footer_auxiliary',
			hideable: false,
			map: this.map
		}),
		new gc.gis.control.OLScaleBar({
			id: '#gc_map_scale_bar',
			className: 'gc-footer-component',
			target: '#gc_footer_main',
			hideable: false,
			map: this.map
		}),
		new gc.gis.control.OLSnap({
			id: '#gc_map_snap',
			map: this.map
		}),
		new gc.gis.control.GCMapLoadingIndicator({
			target: '#gc_map_loading_indicator',
			hideable: false,
			map: this.map
		}),
		new gc.gis.control.GCMapLoadingTimer({
			target: '#gc_map_loading_timer',
			hideable: false,
			map: this.map
		}),
		new gc.gis.control.GCMapGeolocator({
			target: '#gc_map_geolocator',
			//target: this.ui.mapToolbarRotate,
			map: this.map
		}),
		new gc.gis.control.OLZoomInOutBox({
			target: this.ui.mapToolbarZoom,
			map: this.map
		}),
		new gc.gis.control.GCZoomReset({
			target: this.ui.mapToolbarZoom,
			map: this.map
		}),
		new gc.gis.control.GCRotate({
			target: '#gc_map_rotate',
			//target: this.ui.mapToolbarRotate,
			map: this.map
		}),
		new gc.gis.control.GCAttribution({
			id: '#gc_map_attribution',
			className: 'gc-footer-component',
			target: '#gc_footer_auxiliary',
			hideable: false,
			map: this.map
		}),
        new gc.gis.control.GCNotificationManager({
            map: this.map
		}),
		new gc.gis.control.GCLayerTooltipManager({
			map: this.map,
			serviceFactory: this.serviceFactory
		}),
		new gc.gis.control.GCBackgroundLayerSwitcher({
			map: this.map,
			enableVoidLayer: gc.gis.GCUtil.parseBool(this.config.defaults.backgroudLayers.enableVoidLayer, true)
		})
	];

    if (this.env.fullscreenAvailable) {
    	controls.push(new gc.gis.control.GCMapFullscreen({
		    target: this.ui.mapToolbarZoom,
            hideable: false,
			map: this.map
		}));
	}

	if (this.config.defaults.enabledControls)
	{
		var enabledControls = this.config.defaults.enabledControls;
		if (enabledControls.session_viewport)
		{
            controls.push(new gc.gis.control.GCSessionViewport({
                map: this.map
            }));
        }
	}

    this.map.setControls(controls);
};

/**
 * @private
 */
gc.gis.GCApplication.prototype._initInitialView = function ()
{
    var viewport;
	var ctrl = this.map.getControlByType(gc.gis.control.GCSessionViewport);

	// Ansicht laden
	if (this.config.defaults.initialView)
	{
		var view = this.config.defaults.initialView;
        if (ctrl && ctrl instanceof gc.gis.control.GCSessionViewport)
        {
        	// Projektion der Ansicht beibehalten
        	var t_projection;
        	if (view.viewport && view.viewport.projection)
			{
                t_projection = view.viewport.projection;
			}
            viewport = ctrl.getSessionViewport(t_projection);
        }

		this.log.debug(this, 'INIT: Loading initial view... ');
		this.setView(view, viewport);
		this.log.debug(this, 'INIT: Loading initial view... Done.');
	}
	else if (ctrl && ctrl instanceof gc.gis.control.GCSessionViewport)
	{
		// Viewport ggf. anpassen, dabei die initiale Projektion beibehalten
		viewport = ctrl.getSessionViewport(this.map.getProjection());
		if (viewport)
		{
            this.map.setViewport(viewport);
		}
	}
};

/**
 * @private
 */
gc.gis.GCApplication.prototype._initBackgorundLayers = function ()
{
	const backgroundLayerConfiguration = this.config.defaults.backgroudLayers.layers || [];
	new gc.gis.map.GCBackgroundLayerInitiator(
		this.map,
		this.request,
		this.mapFactory,
		backgroundLayerConfiguration
	);
};

/**
 * Prüft, ob ein Modul bereits geladen wurde.
 *
 * @public
 * @param {string} alias Alias des zu überprüfenden Moduls
 * @return {boolean}
 */
gc.gis.GCApplication.prototype.isModuleLoaded = function(alias)
{
	return this.modules[alias] !== undefined;
};

/**
 * Liefert die Modulinstanz.
 *
 * @public
 * @param {string} alias Alias des zu überprüfenden Moduls
 * @return {gc.gis.module.GCAbstractModule|undefined}
 */
gc.gis.GCApplication.prototype.getModule = function(alias)
{
	return this.modules[alias];
};

/**
 * Führt eine Callback aus, wenn ein Modul entweder bereits geladen ist oder
 * wartet darauf bis es geladen wurde und führt sie dann aus.
 *
 * @public
 * @param {gc.gis.module.GCAbstractModule} callee Aufrufendes Modul
 * @param {string} alias Alias des Zielmoduls
 * @param {function} callback Auszuführende Funktion
 * @param {gc.gis.module.GCAbstractModule} callback.module Instanz des Zielmoduls
 */
gc.gis.GCApplication.prototype.onModuleAvailable = function(callee, alias, callback )
{
	var self = this;

	if( this.isModuleLoaded( alias ) )
	{
		this.log.debug( callee, 'Module "'+ alias +'" already loaded. Executing callback ... ');
		callback.call( callee, this.modules[alias] );
	}
	else
	{
		this.log.debug( callee, 'Module "'+ alias +'" not loaded. Executing callback when it becomes available ... ');
		this.on( gc.gis.GCApplicationEvent.MODULE_LOADED, callee, function(evt ) {
			if( evt.alias === alias )
			{
				self.log.debug( callee, 'Module "'+ alias +'" loaded. Executing delayed callback ... ');
				callback.call( callee, evt.instance );
			}
		});
	}
};

/**
 * @public
 * @param {Object} view
 * @param {string} view.name
 * @param {gc.gis.map.GCMapViewport} view.viewport
 * @param {gc.gis.map.GCMapViewport} [opt_viewport] optimaler Viewport, der den Viewport der Ansicht überschreibt
 * @param {gc.gis.layer.GCAbstractLayer[]} view.layers
 */
gc.gis.GCApplication.prototype.setView = function(view, opt_viewport)
{
	gc.gis.assert.isSet(view);

	if(view)
	{
		this.log.debug(this, 'Loading view "' + view.name + '" ... ');
		this.lastViewport = view;

		// Layer laden
		if(view.layers)
		{
			this.map.removeAllLayers();

			this.log.debug(this, 'Loading ' + view.layers.length + ' layers into view "' + view.name + '" ... ');

			for(var i=0; i<view.layers.length; i++)
            //for(var i=view.layers.length-1; i>=0; i--)
			{
				this.loadLayer(view.layers[i]);
			}
		}

		// Viewport laden - keine Animation beim Setzen der Startansicht, in Kombination mit einer Gebietsbeschränkung verhaspelt sich OL mit der Startauflösung
		if (opt_viewport)
		{
            this.map.setViewport(opt_viewport, false);
		}
		else if(view.viewport)
		{
			this.map.setViewport(view.viewport, false);
		}

		this.map.updateMap();

		this._fireEvent( gc.gis.GCApplicationEvent.VIEW_LOADED, {
			view: view
		});
		gc.gis.iface.log.info(self, 'Ansicht "' + view.name + '" geladen.');
	}
	else
	{
		this.log.warn( this, 'Kann nicht in leere Ansicht wechseln.');
	}
};

/**
 * Achtung: Diese Ansicht repräsentiert die zuletzt angewählte und beinhaltet
 * nicht zwangsläufig den aktuellen Kartenausschnitt und/oder geladene Layer!
 *
 * @public
 * @return {Object<string,*>}
 */
gc.gis.GCApplication.prototype.getLastView = function()
{
	return this.lastViewport;
};

/**
 * @public
 * @param {Object} options Optionen (siehe Optionen in
 *   {@link gc.gis.layer.GCAbstractLayer},
 *   {@link gc.gis.layer.GCLayer},
 *   {@link gc.gis.layer.GCRasterLayer} und
 *   {@link gc.gis.layer.GCVectorLayer})
 * @param {gc.gis.layer.GCGroupLayer} [opt_group] Optionaler Gruppenlayer, zu dem der
 *   zu ladende Layer hinzugefügt werden soll
 * @see gc.gis.map.GCMapView#loadLayer
 */
gc.gis.GCApplication.prototype.loadLayer = function(options, opt_group )
{
	this.map.loadLayer( options, opt_group );
};
